import React, { useMemo, useRef, useEffect } from 'react'

// Compact scrolling log of the step's raw telemetry, click-to-seek.
// Colour groups match the event families in constants/telemetryEvents.js.
const GROUP = (type) => {
  if (type.startsWith('CODE_')) return { key: 'code', color: '#3b82f6' }
  if (type.startsWith('CHAT_')) return { key: 'chat', color: '#a855f7' }
  if (type.startsWith('TERMINAL_')) return { key: 'terminal', color: '#10b981' }
  if (type.startsWith('TASK_') || type.startsWith('TEST_')) return { key: 'task', color: '#f59e0b' }
  if (type.startsWith('MOUSE_')) return { key: 'mouse', color: '#94a3b8' }
  return { key: 'session', color: '#ef4444' }
}

const clip = (s, n = 70) => {
  const t = String(s ?? '').replace(/\s+/g, ' ').trim()
  return t.length > n ? t.slice(0, n) + '…' : t
}

// One-line human summary per event family.
function summarize (e) {
  const p = e.payload || {}
  switch (e.type) {
    case 'SESSION_START': return `${p.window_width}×${p.window_height}`
    case 'TASK_COMPLETED': return `submitted · ${(p.code || '').length} chars`
    case 'TASK_TIMEOUT': return `timer expired · ${(p.code || '').length} chars`
    case 'MOUSE_MOVE': return p.region
    case 'MOUSE_CLICK': return `${p.region} · <${String(p.tag || '').toLowerCase()}>`
    case 'TAB_STATE': return p.visible ? 'returned to tab' : 'left tab'
    case 'WINDOW_RESIZE': return `${p.width}×${p.height}`
    case 'PANEL_RESIZE': return `${p.panel} → ${Math.round(p.newSize)}px`
    case 'TERMINAL_RUN': return `${p.code_length} chars`
    case 'TERMINAL_OUTPUT': return clip(p.output)
    case 'TERMINAL_ERROR': return `${p.type}: ${clip(p.message, 50)}`
    case 'CHAT_QUERY': return clip(p.text)
    case 'CHAT_RESPONSE': return `${p.latency_ms}ms · ${clip(p.content, 50)}`
    case 'CHAT_TYPE': return clip(p.text, 40)
    case 'CHAT_ERROR': return clip(p.error)
    case 'CODE_PASTE': return clip((p.changes || []).map(c => c.text).join(''), 50)
    default:
      if (e.type.startsWith('CODE_')) {
        const ins = (p.changes || []).map(c => c.text).join('')
        return ins ? clip(ins, 40) : `${(p.code || '').length} chars`
      }
      return clip(p.selected_text || '')
  }
}

export default function StudyEventLog ({ events, startTime, currentTime, onSeek, filter, setFilter }) {
  const scrollRef = useRef(null)

  const rows = useMemo(() => events.map(e => ({
    ...e,
    offset: e.timestamp - startTime,
    group: GROUP(e.type)
  })), [events, startTime])

  const shown = useMemo(
    () => filter === 'all' ? rows : rows.filter(r => r.group.key === filter),
    [rows, filter]
  )

  // Index of the last event at or before the playhead
  const activeIdx = useMemo(() => {
    let idx = -1
    for (let i = 0; i < shown.length; i++) { if (shown[i].offset <= currentTime) idx = i; else break }
    return idx
  }, [shown, currentTime])

  useEffect(() => {
    const el = scrollRef.current?.querySelector('[data-active="1"]')
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [activeIdx])

  const counts = useMemo(() => {
    const c = { all: rows.length }
    for (const r of rows) c[r.group.key] = (c[r.group.key] || 0) + 1
    return c
  }, [rows])

  const t = (ms) => `${Math.floor(ms / 60000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}`

  return (
    <div className="srp-log">
      <div className="srp-log__filters">
        {['all', 'code', 'terminal', 'chat', 'mouse', 'task', 'session'].map(k => (
          <button
            key={k}
            className={`srp-chip${filter === k ? ' is-on' : ''}`}
            onClick={() => setFilter(k)}
            disabled={!counts[k]}
          >
            {k} <span className="srp-chip__n">{counts[k] || 0}</span>
          </button>
        ))}
      </div>
      <div className="srp-log__scroll" ref={scrollRef}>
        {shown.map((r, i) => (
          <div
            key={i}
            data-active={i === activeIdx ? '1' : '0'}
            className={`srp-row${i === activeIdx ? ' is-active' : ''}${r.offset > currentTime ? ' is-future' : ''}`}
            onClick={() => onSeek(r.offset)}
          >
            <span className="srp-row__t">{t(r.offset)}</span>
            <span className="srp-row__dot" style={{ background: r.group.color }} />
            <span className="srp-row__type">{r.type}</span>
            <span className="srp-row__sum">{summarize(r)}</span>
          </div>
        ))}
        {!shown.length && <div className="srp-empty">No events in this filter.</div>}
      </div>
    </div>
  )
}
